import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 * @property {HTMLDivElement} [loadPreviousWrapper] - The load previous button wrapper.
 * @property {HTMLButtonElement} [loadPreviousButton] - The load previous button.
 * @property {HTMLDivElement} [loadMoreWrapper] - The load more button wrapper.
 * @property {HTMLButtonElement} [loadMoreButton] - The load more button.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #shouldObservePrevious = true;

  /** @type {boolean} */
  #shouldObserveNext = true;

  /** @type {number} */
  #autoLoadedPagesCount = 0;

  /** @type {boolean} */
  #isRenderingNextPage = false;

  /** @type {boolean} */
  #isRenderingPreviousPage = false;

  /** @type {number} */
  static AUTO_LOAD_LIMIT = 3;

  /** @type {Set<string>} */
  #renderedProductIds = new Set();

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    // Initialize deduplication Set with products already on the page
    this.#initializeRenderedProductIds();

    // Check if we're on page 2+ (detect from URL or cards data-page attribute)
    const isOnPageTwoOrHigher = this.#isOnPageTwoOrHigher();

    if (isOnPageTwoOrHigher) {
      // Don't auto-observe previous — silently restore all prior pages
      this.#shouldObservePrevious = false;
      this.#restorePreviousPages();
    } else {
      // On page 1, ensure button is hidden and fetch previous page as normal (though there won't be one)
      const { loadPreviousWrapper } = this.refs;
      if (loadPreviousWrapper) {
        loadPreviousWrapper.style.display = 'none';
      }
      this.#fetchPage('previous');
    }

    this.#fetchPage('next');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Only observe previous if we should (not on page 2+)
    if (viewMorePrevious && this.#shouldObservePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    // Only observe next if we should (not after auto-load limit)
    if (viewMoreNext && this.#shouldObserveNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    // Prevent concurrent renders that cause grid flashing
    if (this.#isRenderingNextPage) return;
    this.#isRenderingNextPage = true;

    try {
      const { grid } = this.refs;

      if (!grid) return;

      const nextPage = this.#getPage('next');

      if (!nextPage || !this.#shouldUsePage(nextPage)) return;
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        // Show skeleton loading indicator
        this.#showSkeletonLoader('next');

        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);

        // Hide skeleton loading indicator
        this.#hideSkeletonLoader('next');

        if (!nextPageItemElements) return;
      }

      // Filter out duplicate products (important for search results with non-deterministic ordering)
      const uniqueElements = [];
      let duplicateCount = 0;

      for (const element of nextPageItemElements) {
        const productId = this.#getProductIdFromCard(element);

        if (!productId || !this.#renderedProductIds.has(productId)) {
          uniqueElements.push(element);
          if (productId) {
            this.#renderedProductIds.add(productId);
          }
        } else {
          duplicateCount++;
        }
      }

      // Only append if we have unique elements
      if (uniqueElements.length === 0) {
        return;
      }

      grid.append(...uniqueElements);

      // Only process aspect ratios if helper is initialized
      if (this.#aspectRatioHelper) {
        this.#aspectRatioHelper.processNewElements();
      }

      history.replaceState('', '', nextPage.url.toString());

      // Increment auto-load counter
      this.#autoLoadedPagesCount++;

      // Check if we've hit a multiple of 3 (3, 6, 9, 12, etc.)
      if (this.#autoLoadedPagesCount > 0 && this.#autoLoadedPagesCount % PaginatedList.AUTO_LOAD_LIMIT === 0) {

        // Stop auto-loading temporarily
        this.#shouldObserveNext = false;

        // Unobserve next trigger
        const { viewMoreNext } = this.refs;
        if (viewMoreNext && this.infinityScrollObserver) {
          this.infinityScrollObserver.unobserve(viewMoreNext);
        }

        // Show Load More button
        this.#showLoadMoreButton();
      } else {
        // Continue auto-loading
        requestIdleCallback(() => {
          this.#fetchPage('next');
        });
      }
    } finally {
      this.#isRenderingNextPage = false;
    }
  }

  async #renderPreviousPage() {
    // Prevent concurrent renders that cause grid flashing
    if (this.#isRenderingPreviousPage) return;
    this.#isRenderingPreviousPage = true;

    try {
      const { grid } = this.refs;

      if (!grid) return;

      const previousPage = this.#getPage('previous');
      if (!previousPage || !this.#shouldUsePage(previousPage)) return;

      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) {
        // Show skeleton loading indicator
        this.#showSkeletonLoader('previous');

        const promise = new Promise((res) => {
          this.#resolvePreviousPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('previous');

        await promise;
        previousPageItemElements = this.#getGridForPage(previousPage.page);

        // Hide skeleton loading indicator
        this.#hideSkeletonLoader('previous');

        if (!previousPageItemElements) return;
      }

      // Filter out duplicate products (important for search results with non-deterministic ordering)
      const uniqueElements = [];
      let duplicateCount = 0;

      for (const element of previousPageItemElements) {
        const productId = this.#getProductIdFromCard(element);

        if (!productId || !this.#renderedProductIds.has(productId)) {
          uniqueElements.push(element);
          if (productId) {
            this.#renderedProductIds.add(productId);
          }
        } else {
          duplicateCount++;
        }
      }

      // Only prepend if we have unique elements
      if (uniqueElements.length === 0) {
        return;
      }

      // Store the current scroll position and height of the first element
      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

      // Prepend the new elements
      grid.prepend(...uniqueElements);

      // Only process aspect ratios if helper is initialized
      if (this.#aspectRatioHelper) {
        this.#aspectRatioHelper.processNewElements();
      }

      history.replaceState('', '', previousPage.url.toString());

      // Calculate and adjust scroll position to maintain the same view
      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: 'instant',
        });
      }

      requestIdleCallback(() => {
        this.#fetchPage('previous');
      });
    } finally {
      this.#isRenderingPreviousPage = false;
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { grid } = this.refs;
    const isPrevious = type === 'previous';

    if (!grid) return;

    // Query DOM directly to avoid stale refs after prepending/appending
    const allCards = grid.querySelectorAll('[ref="cards[]"]');
    if (allCards.length === 0) return;

    const targetCard = isPrevious ? allCards[0] : allCards[allCards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);

    // For page 1, remove the page parameter entirely (canonical URL)
    if (page === 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', page.toString());
    }

    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Show skeleton loading indicator
   * @param {"previous" | "next"} position - Where to show the skeleton
   */
  #showSkeletonLoader(position) {
    const { grid } = this.refs;
    if (!grid) return;

    // Create skeleton cards and insert them directly into the existing grid
    const skeletonCards = Array.from({ length: 12 }, (_, i) => {
      const skeletonItem = document.createElement('li');
      skeletonItem.className = `product-grid__item product-grid-skeleton__item`;
      skeletonItem.setAttribute('data-skeleton-item', position);
      skeletonItem.innerHTML = `
        <div class="product-grid-skeleton__card">
          <div class="product-grid-skeleton__image"></div>
          <div class="product-grid-skeleton__content">
            <div class="product-grid-skeleton__title"></div>
            <div class="product-grid-skeleton__price"></div>
          </div>
        </div>
      `;
      return skeletonItem;
    });

    if (position === 'next') {
      skeletonCards.forEach(card => grid.appendChild(card));
    } else {
      skeletonCards.reverse().forEach(card => grid.insertBefore(card, grid.firstChild));
    }
  }

  /**
   * Hide skeleton loading indicator
   * @param {"previous" | "next"} position - Which skeleton to hide
   */
  #hideSkeletonLoader(position) {
    const skeletonItems = this.querySelectorAll(`[data-skeleton-item="${position}"]`);
    skeletonItems.forEach(item => {
      item.classList.add('is-removing');
      setTimeout(() => item.remove(), 300);
    });
  }

  /**
   * Initialize the Set of rendered product IDs with products already on the page
   */
  #initializeRenderedProductIds() {
    const { grid } = this.refs;
    if (!grid) return;

    const existingCards = grid.querySelectorAll('[ref="cards[]"]');
    existingCards.forEach((card) => {
      const productId = this.#getProductIdFromCard(card);
      if (productId) {
        this.#renderedProductIds.add(productId);
      }
    });
  }

  /**
   * Extract a unique product identifier from a card element
   * @param {Element} card - The product card element
   * @returns {string | null} The product ID or null if not found
   */
  #getProductIdFromCard(card) {
    // Try data-product-id first
    if (card.dataset.productId) {
      return card.dataset.productId;
    }

    // Try to find product link and extract handle/ID from URL
    const productLink = card.querySelector('a[href*="/products/"]');
    if (productLink) {
      const href = productLink.getAttribute('href');
      if (href) {
        // Extract product handle from URL like /products/product-handle
        const match = href.match(/\/products\/([^?#]+)/);
        if (match && match[1]) {
          return match[1];
        }
      }
    }

    // Fallback: use the entire card's innerHTML as a hash (not ideal but works)
    // This is a last resort for deduplication
    return null;
  }

  /**
   * Check if we're on page 2 or higher
   * @returns {boolean}
   */
  #isOnPageTwoOrHigher() {
    // Check URL params first
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get('page');
    if (pageParam && parseInt(pageParam) >= 2) {
      return true;
    }

    // Fallback: check first card's data-page attribute
    const { cards } = this.refs;
    if (Array.isArray(cards) && cards.length > 0) {
      const firstCardPage = parseInt(cards[0].dataset.page);
      if (firstCardPage >= 2) {
        return true;
      }
    }

    return false;
  }

  /**
   * On back-navigation or reload to ?page=N, silently fetch all pages 1…N-1,
   * prepend them to the grid, and scroll so the originally-visible content
   * stays at the top of the viewport. Normalises the URL back to page 1.
   */
  async #restorePreviousPages() {
    const { grid } = this.refs;
    if (!grid) return;

    const firstOriginalCard = grid.querySelector('[ref="cards[]"]');
    if (!firstOriginalCard) return;

    const currentPage = parseInt(firstOriginalCard.dataset.page) || 2;
    if (currentPage <= 1) return;

    // Fetch all previous pages in parallel
    const fetchPromises = [];
    for (let p = 1; p < currentPage; p++) {
      const url = new URL(window.location.href);
      if (p === 1) {
        url.searchParams.delete('page');
      } else {
        url.searchParams.set('page', p.toString());
      }
      url.hash = '';
      fetchPromises.push(this.#fetchSpecificPage(p, url));
    }
    await Promise.all(fetchPromises);

    // Measure where the originally-loaded content starts before we prepend
    const scrollTop = window.scrollY;
    const oldTop = firstOriginalCard.getBoundingClientRect().top + window.scrollY;

    // Collect all elements in page order
    const allNewElements = [];
    for (let p = 1; p < currentPage; p++) {
      const pageElements = this.#getGridForPage(p);
      if (!pageElements) continue;
      for (const element of pageElements) {
        const productId = this.#getProductIdFromCard(element);
        if (!productId || !this.#renderedProductIds.has(productId)) {
          allNewElements.push(element);
          if (productId) this.#renderedProductIds.add(productId);
        }
      }
    }

    if (allNewElements.length === 0) return;

    grid.prepend(...allNewElements);

    if (this.#aspectRatioHelper) {
      this.#aspectRatioHelper.processNewElements();
    }

    // Adjust scroll so the originally-visible content stays at the same position
    const newTop = firstOriginalCard.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: scrollTop + (newTop - oldTop), behavior: 'instant' });

    // Normalise URL to canonical page 1
    const canonicalUrl = new URL(window.location.href);
    canonicalUrl.searchParams.delete('page');
    history.replaceState('', '', canonicalUrl.toString());
  }

  /**
   * Show the Load Previous button and wire up click handler
   */
  #showLoadPreviousButton() {
    const { loadPreviousWrapper, loadPreviousButton } = this.refs;

    if (!loadPreviousWrapper || !loadPreviousButton) return;

    // Check if we can actually load a previous page
    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) {
      // No previous page available, keep button hidden
      loadPreviousWrapper.style.display = 'none';
      return;
    }

    // Show the button
    loadPreviousWrapper.style.display = '';

    // Create click handler that can be called multiple times
    const handleClick = async () => {
      // Disable button during load
      loadPreviousButton.disabled = true;

      // Render previous page
      await this.#renderPreviousPage();

      // Hide button after loading - check if we're now on page 1
      const currentPage = this.#getCurrentPage();
      if (currentPage <= 1) {
        loadPreviousWrapper.style.display = 'none';

        // Remove the click handler since button is now hidden
        loadPreviousButton.removeEventListener('click', handleClick);
      } else {
        // Re-enable button for next previous page
        loadPreviousButton.disabled = false;
      }

      // Note: Previous pages should NEVER auto-load, only via button press
    };

    // Wire up click handler (not once: true, so it can be clicked multiple times)
    loadPreviousButton.addEventListener('click', handleClick);
  }

  /**
   * Show the Load More button and wire up click handler
   */
  #showLoadMoreButton() {
    const { loadMoreWrapper, loadMoreButton } = this.refs;

    if (!loadMoreWrapper || !loadMoreButton) return;

    // Check if we can actually load a next page
    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage)) {
      // No next page available, keep button hidden
      loadMoreWrapper.style.display = 'none';
      return;
    }

    // Show the button and reset disabled state
    loadMoreWrapper.style.display = '';
    loadMoreButton.disabled = false;

    // Create click handler
    const handleClick = async () => {
      // Disable button during load
      loadMoreButton.disabled = true;

      // Hide the button immediately
      loadMoreWrapper.style.display = 'none';

      // Manually render the next page (this increments counter)
      await this.#renderNextPage();

      // Reset counter to 0 to start a new 3-page cycle
      this.#autoLoadedPagesCount = 0;

      // Re-enable auto-loading for the next 3 pages
      this.#shouldObserveNext = true;

      // Re-observe the viewMoreNext trigger to continue auto-loading
      // (renderNextPage already called requestIdleCallback to fetch the next page)
      const { viewMoreNext } = this.refs;
      if (viewMoreNext && this.infinityScrollObserver) {
        this.infinityScrollObserver.observe(viewMoreNext);
      }

      // Remove the click handler since button was hidden
      loadMoreButton.removeEventListener('click', handleClick);
    };

    // Wire up click handler
    loadMoreButton.addEventListener('click', handleClick);
  }

  /**
   * Get the current page number from the first card
   * Query DOM directly to avoid stale refs after prepending
   * @returns {number}
   */
  #getCurrentPage() {
    const { grid } = this.refs;
    if (!grid) return 1;

    // Query the DOM directly instead of using refs.cards which may be stale
    const firstCard = grid.querySelector('[ref="cards[]"]');
    if (firstCard && firstCard.dataset.page) {
      return parseInt(firstCard.dataset.page) || 1;
    }
    return 1;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Reset to page 1 behavior after filtering
    this.#shouldObservePrevious = true;
    this.#shouldObserveNext = true;
    this.#autoLoadedPagesCount = 0;
    this.#isRenderingNextPage = false;
    this.#isRenderingPreviousPage = false;

    // Clear deduplication tracking - will be re-initialized with filtered results
    this.#renderedProductIds.clear();

    // Hide Load Previous button if it's showing
    const { loadPreviousWrapper } = this.refs;
    if (loadPreviousWrapper) {
      loadPreviousWrapper.style.display = 'none';
    }

    // Hide Load More button if it's showing
    const { loadMoreWrapper } = this.refs;
    if (loadMoreWrapper) {
      loadMoreWrapper.style.display = 'none';
    }

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Re-initialize deduplication tracking with new filtered products
        this.#initializeRenderedProductIds();

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
